import { useCallback, useState, useEffect, useMemo, useRef } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { useParams } from 'react-router-dom';
import {
	getUserFacingErrorMessage,
	makeRequest,
} from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import { HTTP_METHOD } from '@dealstack/shared/lib/common/dataExchange/common';
import { getGlobalConfig } from '@dealstack/frontend/shared/globalConfig';
import { apiClient } from '@dealstack/frontend/api/client';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { DocumentAnalysisData } from '@dealstack/shared/lib/common/business/documentAnalysis';
import { FieldValue } from '@dealstack/shared/lib/aiClient/types';
import { TaskStatus } from '@dealstack/shared/lib/generated';
import {
	startDocumentExtraction,
	getDocumentExtractionStatus,
	getDealBatches,
	getBatchTransactions,
	type ExtractionDocumentStatus,
	type BatchListDocumentItem,
} from '@dealstack/frontend/app/services/DocumentExtraction.service';
import type { RichCanonicalEntity } from '@dealstack/shared/lib/temporal/documentExtraction/types';
import {
	createPollingRefetchInterval,
	isPollingStalled,
} from '@dealstack/frontend/shared/dataExchange/reactQuery/pollingRefetchInterval';

interface UseTransactionFromDocumentModalControllerArgs {
	knownSecurities?: RichCanonicalEntity[];
	knownPersons?: RichCanonicalEntity[];
}

export interface DocumentAnalysisResponse {
	error: boolean;
	errorMessage: string;
	errorNode: string;
	data: DocumentAnalysisData[];
	success: boolean;
}

export interface ProcessedDocument {
	file: File;
	fileUrl: string | null;
	analysisResult: DocumentAnalysisResponse | null;
	isProcessing: boolean;
	hasError: boolean;
}

export type TransactionFromDocumentModalControllerState = ReturnType<
	typeof useTransactionFromDocumentModalController
>;

const applyTransactionFieldUpdate = (
	transaction: DocumentAnalysisData,
	fieldName: keyof DocumentAnalysisData,
	value: string | number | null,
	getSelectedIndex: (fieldName: 'buyers' | 'sellers') => number
): DocumentAnalysisData => {
	if (fieldName === 'buyers' || fieldName === 'sellers') {
		const fieldArray = transaction[fieldName];
		if (!Array.isArray(fieldArray)) {
			return transaction;
		}
		const selectedIndex = getSelectedIndex(fieldName);
		const updatedArray = fieldArray.map((item, idx) =>
			idx === selectedIndex
				? {
						value,
						confidence: value !== item?.value ? -1 : item?.confidence || 0,
					}
				: item
		);
		return { ...transaction, [fieldName]: updatedArray };
	}
	const fieldValue = transaction[fieldName] as FieldValue;
	return {
		...transaction,
		[fieldName]: {
			value,
			confidence: value !== fieldValue?.value ? -1 : fieldValue?.confidence || 0,
		},
	};
};

export const useTransactionFromDocumentModalController = ({
	knownSecurities,
	knownPersons,
}: UseTransactionFromDocumentModalControllerArgs = {}) => {
	const tCapManage = useTranslationCapManage();
	const queryClient = useQueryClient();
	const { dealId, personId } = useParams<{ dealId: string; personId: string }>();
	const [documents, setDocuments] = useState<ProcessedDocument[]>([]);
	const [selectedDocumentIndex, setSelectedDocumentIndexState] = useState(0);
	const [selectedTransactionIndex, setSelectedTransactionIndexState] = useState(0);
	const [isProcessing, setIsProcessing] = useState(false);
	const [processingStep, setProcessingStep] = useState<'uploading' | 'analyzing' | null>(null);
	const [currentProcessingIndex, setCurrentProcessingIndex] = useState(0);
	const [editedValues, setEditedValues] = useState<Partial<DocumentAnalysisData>>({});
	const [isEditMode, setIsEditMode] = useState(false);
	const [showBulkImport, setShowBulkImport] = useState(false);
	const [buyerSellerSelections, setBuyerSellerSelections] = useState<Record<string, number>>({});

	// Extraction state
	const [extractionTaskId, setExtractionTaskId] = useState<string | null>(null);
	// List-seeded rows shown before the first poll arrives. The displayed
	// `extractionDocuments` below prefers the live poll response over this.
	const [seededExtractionDocuments, setSeededExtractionDocuments] = useState<
		ExtractionDocumentStatus[]
	>([]);
	const [batchId, setBatchId] = useState<string | null>(null);
	const [batchTitle, setBatchTitle] = useState<string>('');

	// Batch selection state
	const [selectedBatchId, setSelectedBatchId] = useState<string | null>(null);
	const [selectedBatchStatus, setSelectedBatchStatus] = useState<TaskStatus | null>(null);

	// Batches list query (deal-scoped — batches are not coupled to a person)
	const batchesQuery = useQuery({
		queryKey: ['document-batches', dealId],
		queryFn: ({ signal }) => getDealBatches(dealId ?? '', signal),
		enabled: !!dealId,
	});

	// Batch transactions query (for completed batches)
	const batchTransactionsQuery = useQuery({
		queryKey: ['batch-transactions', selectedBatchId],
		queryFn: ({ signal }) => getBatchTransactions(selectedBatchId ?? '', dealId ?? '', signal),
		enabled: !!selectedBatchId && !!dealId && selectedBatchStatus === TaskStatus.DONE,
	});

	const selectBatch = async (
		batchId: string,
		status: TaskStatus,
		batchDocuments?: BatchListDocumentItem[]
	) => {
		setSelectedBatchId(batchId);
		setSelectedBatchStatus(status);

		if (status !== TaskStatus.DONE) {
			// Seed extraction documents from batch list data so the UI
			// immediately shows correct file names and count before the
			// first poll response arrives
			if (batchDocuments && batchDocuments.length > 0) {
				setSeededExtractionDocuments(
					batchDocuments.map((doc) => ({
						documentId: doc.id,
						fileName: doc.fileName,
						workflowId: doc.workflowId,
						status: doc.status as ExtractionDocumentStatus['status'],
					}))
				);
			}

			// In-progress batch: use batchId for polling
			setExtractionTaskId(batchId);
			setIsProcessing(true);
			setProcessingStep('analyzing');
		}
	};

	const deselectBatch = () => {
		setSelectedBatchId(null);
		setSelectedBatchStatus(null);
		setExtractionTaskId(null);
		setIsProcessing(false);
		setProcessingStep(null);
		setSeededExtractionDocuments([]);
	};

	const currentDocument = documents[selectedDocumentIndex] || null;
	const uploadedFile = currentDocument?.file || null;
	const uploadedFileUrl = currentDocument?.fileUrl || null;
	const analysisResult = currentDocument?.analysisResult || null;

	// editedValues holds per-field user overrides (Partial<DocumentAnalysisData>).
	// Reset it when the user navigates to a different document or transaction so
	// edits from the prior selection don't leak across. The wrappers below are
	// the only entry points that change the selection; resetting here keeps
	// "selection change → drop overrides" as a single atomic action and avoids
	// a useEffect that would re-shape state from query data on every refetch.
	const setSelectedDocumentIndex = useCallback((idx: number) => {
		setSelectedDocumentIndexState((prev) => {
			if (prev !== idx) {
				setEditedValues({});
			}
			return idx;
		});
	}, []);
	const setSelectedTransactionIndex = useCallback((idx: number) => {
		setSelectedTransactionIndexState((prev) => {
			if (prev !== idx) {
				setEditedValues({});
			}
			return idx;
		});
	}, []);

	// Extraction status polling
	const extractionStatusQuery = useQuery({
		queryKey: [`/document-parsing/extraction/${extractionTaskId || ''}/status`, dealId ?? ''],
		queryFn: getDocumentExtractionStatus,
		enabled: !!extractionTaskId,
		refetchInterval: !extractionTaskId
			? false
			: createPollingRefetchInterval({
					isTerminal: (data) =>
						data?.status === TaskStatus.DONE || data?.status === TaskStatus.ERROR,
				}),
		// Polling continues through transient errors — without this the global
		// query cache would toast every failed poll cycle.
		meta: { suppressErrorToast: true },
	});

	// Polling gave up after repeated fetch errors; the modal renders a
	// "refresh to check status" notice off this instead of freezing.
	const isExtractionPollingStalled =
		!!extractionTaskId && isPollingStalled(extractionStatusQuery);

	// Displayed documents: prefer the live poll response, falling back to the
	// list-seeded rows shown before the first poll. Derived here rather than
	// mirrored into state from a useEffect (per the "no data shaping in
	// useEffect" convention in CLAUDE.md).
	const extractionDocuments = useMemo<ExtractionDocumentStatus[]>(
		() => extractionStatusQuery.data?.documents ?? seededExtractionDocuments,
		[extractionStatusQuery.data?.documents, seededExtractionDocuments]
	);

	// Handle extraction completion — sync batch info on first receipt only via refs
	const batchIdRef = useRef(batchId);
	batchIdRef.current = batchId;
	const batchTitleRef = useRef(batchTitle);
	batchTitleRef.current = batchTitle;

	useEffect(() => {
		if (!extractionStatusQuery.data) {
			return;
		}

		const statusData = extractionStatusQuery.data;

		// Sync batch info from status response (only on first receipt)
		if (statusData.batchId && !batchIdRef.current) {
			setBatchId(statusData.batchId);
		}
		if (statusData.batchTitle && !batchTitleRef.current) {
			setBatchTitle(statusData.batchTitle);
		}

		if (statusData.status === TaskStatus.DONE) {
			// Keep isProcessing true so the modal shows completion state
			// Don't clear extractionTaskId here — the polling already stops
			// via refetchInterval returning false, and clearing it would
			// change the query key, losing the cached status data.
		} else if (statusData.status === TaskStatus.ERROR) {
			setIsProcessing(false);
			setProcessingStep(null);
			setCurrentProcessingIndex(0);
			setExtractionTaskId(null);

			showToastV2({
				state: 'error',
				title: tCapManage('transactions.add_from_document.error_title'),
				message:
					statusData.error || tCapManage('transactions.add_from_document.upload_failed'),
			});
		} else if (statusData.status === TaskStatus.IN_PROGRESS) {
			setProcessingStep('analyzing');
			setCurrentProcessingIndex(statusData.totalProcessed);
		}
	}, [extractionStatusQuery.data, tCapManage]);

	// Stop polling when the component unmounts so React Query doesn't
	// keep firing requests in the background after the modal closes.
	const extractionTaskIdRef = useRef(extractionTaskId);
	extractionTaskIdRef.current = extractionTaskId;
	const dealIdRef = useRef(dealId);
	dealIdRef.current = dealId;
	useEffect(() => {
		return () => {
			const taskId = extractionTaskIdRef.current;
			if (taskId) {
				void queryClient.cancelQueries({
					queryKey: [
						`/document-parsing/extraction/${taskId}/status`,
						dealIdRef.current ?? '',
					],
				});
			}
			setExtractionTaskId(null);
		};
	}, [queryClient]);

	// Start extraction mutation
	const startExtractionMutation = useMutation({ mutationFn: startDocumentExtraction });

	const handleFileDrop = async (files: File[]) => {
		if (files.length === 0) {
			return;
		}

		// Validate all files first
		const validFiles: File[] = [];
		for (const file of files) {
			if (_validateFileSize(file) && _validateFileType(file)) {
				validFiles.push(file);
			}
		}

		if (validFiles.length === 0) {
			return;
		}

		// Add all valid files to the documents list
		const newDocuments: ProcessedDocument[] = validFiles.map((file) => ({
			file,
			fileUrl: null,
			analysisResult: null,
			isProcessing: false,
			hasError: false,
		}));

		setDocuments((prev) => [...prev, ...newDocuments]);
		_showFileUploadSuccess(`${validFiles.length} file(s) uploaded`);
	};

	const clearFile = () => {
		setDocuments([]);
		setSelectedDocumentIndex(0);
		setSelectedTransactionIndex(0);
		setIsProcessing(false);
		setProcessingStep(null);
		setEditedValues({});
		setIsEditMode(false);
		setShowBulkImport(false);
		setExtractionTaskId(null);
		setSeededExtractionDocuments([]);
		setBatchId(null);
		setBatchTitle('');
	};

	const removeDocument = (index: number) => {
		setDocuments((prev) => {
			const newDocs = prev.filter((_, i) => i !== index);

			if (selectedDocumentIndex >= newDocs.length && newDocs.length > 0) {
				setSelectedDocumentIndex(newDocs.length - 1);
			} else if (newDocs.length === 0) {
				setSelectedDocumentIndex(0);
			}
			return newDocs;
		});
	};

	const updateFieldValue = (
		fieldName: keyof DocumentAnalysisData,
		value: string | number | null
	) => {
		setDocuments((prev) =>
			prev.map((doc, docIdx) => {
				if (docIdx !== selectedDocumentIndex || !doc.analysisResult?.data) {
					return doc;
				}
				const updatedData = doc.analysisResult.data.map((transaction, txIdx) =>
					txIdx === selectedTransactionIndex
						? applyTransactionFieldUpdate(
								transaction,
								fieldName,
								value,
								(fn) =>
									buyerSellerSelections[getSelectionKey(docIdx, txIdx, fn)] ?? 0
							)
						: transaction
				);
				return {
					...doc,
					analysisResult: { ...doc.analysisResult, data: updatedData },
				};
			})
		);

		if (fieldName === 'buyers' || fieldName === 'sellers') {
			const currentTransaction = analysisResult?.data?.[selectedTransactionIndex];
			if (currentTransaction) {
				const fieldArray = currentTransaction[fieldName];
				if (Array.isArray(fieldArray)) {
					const selectionKey = getSelectionKey(
						selectedDocumentIndex,
						selectedTransactionIndex,
						fieldName
					);
					const selectedIndex = buyerSellerSelections[selectionKey] ?? 0;

					const updatedArray = fieldArray.map((item, idx) => {
						if (idx === selectedIndex) {
							return {
								value,
								confidence: -1,
							};
						}
						return item;
					});

					setEditedValues((prev) => ({
						...prev,
						[fieldName]: updatedArray,
					}));
				}
			}
		} else {
			setEditedValues((prev) => ({
				...prev,
				[fieldName]: {
					value,
					confidence: -1,
				},
			}));
		}
	};

	const getFinalValues = () => {
		if (!analysisResult?.data || analysisResult.data.length === 0) {
			return null;
		}

		const currentTransaction = analysisResult.data[selectedTransactionIndex];
		if (!currentTransaction) {
			return null;
		}

		return {
			...currentTransaction,
			...editedValues,
		};
	};

	const getAllTransactions = (): DocumentAnalysisData[] => {
		const allTransactions: DocumentAnalysisData[] = [];

		documents.forEach((doc) => {
			if (doc.analysisResult?.success && doc.analysisResult.data) {
				allTransactions.push(...doc.analysisResult.data);
			}
		});

		return allTransactions;
	};

	const processDocuments = async () => {
		if (documents.length === 0 || !dealId || isProcessing) {
			return;
		}

		setIsProcessing(true);
		setProcessingStep('uploading');

		try {
			// Step 1: Upload all files and get pre-signed URLs
			const uploadedDocs = await _uploadAllDocuments(dealId);

			if (uploadedDocs.length === 0) {
				setIsProcessing(false);
				setProcessingStep(null);
				return;
			}

			// Step 2: Start the Temporal extraction for all documents
			setProcessingStep('analyzing');

			// Default batch title to first document's file name if not set
			const effectiveBatchTitle = batchTitle || uploadedDocs[0]?.fileName || undefined;
			if (effectiveBatchTitle && !batchTitle) {
				setBatchTitle(effectiveBatchTitle);
			}

			const result = await startExtractionMutation.mutateAsync({
				dealId,
				documents: uploadedDocs.map((d) => ({ fileId: d.fileId })),
				batchTitle: effectiveBatchTitle,
				...(personId && { personId }),
				...(knownSecurities && knownSecurities.length > 0 && { knownSecurities }),
				...(knownPersons && knownPersons.length > 0 && { knownPersons }),
			});

			// Step 3: Start polling for status
			setExtractionTaskId(result.taskId);
			if (result.batchId) {
				setBatchId(result.batchId);
			}

			// Mark documents as processing
			setDocuments((prev) =>
				prev.map((d, idx) => {
					const isBeingProcessed = uploadedDocs.some((ud) => ud.index === idx);
					return isBeingProcessed ? { ...d, isProcessing: true } : d;
				})
			);
		} catch (error) {
			setIsProcessing(false);
			setProcessingStep(null);
			setCurrentProcessingIndex(0);
			_showUploadError(error);
		}
	};

	// Computed stats
	const processedDocumentsCount = documents.filter((d) => d.analysisResult !== null).length;
	const hasAnyResults = documents.some((d) => d.analysisResult && !d.analysisResult.error);
	const currentProcessingDocument = documents[currentProcessingIndex - 1] || null;

	// Extraction progress info
	const isExtractionRunning = !!extractionTaskId;
	const extractionProgress = extractionStatusQuery.data
		? {
				totalDocuments: extractionStatusQuery.data.totalDocuments,
				totalProcessed: extractionStatusQuery.data.totalProcessed,
				status: extractionStatusQuery.data.status,
				documents: extractionDocuments,
			}
		: null;

	// Private helper functions

	const _validateFileSize = (file: File): boolean => {
		const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB
		if (file.size > MAX_FILE_SIZE) {
			showToastV2({
				state: 'error',
				title: tCapManage('transactions.add_from_document.error_title'),
				message: tCapManage('transactions.add_from_document.file_too_large'),
			});
			return false;
		}
		return true;
	};

	const _validateFileType = (file: File): boolean => {
		const ALLOWED_TYPES = ['application/pdf'];
		if (!ALLOWED_TYPES.includes(file.type)) {
			showToastV2({
				state: 'error',
				title: tCapManage('transactions.add_from_document.error_title'),
				message: tCapManage('transactions.add_from_document.invalid_file_type'),
			});
			return false;
		}
		return true;
	};

	const _showFileUploadSuccess = (fileName: string): void => {
		showToastV2({
			state: 'success',
			title: tCapManage('transactions.add_from_document.success_title'),
			message: tCapManage('transactions.add_from_document.file_uploaded', {
				fileName,
			}),
		});
	};

	const _uploadFile = async (
		file: File,
		uploadDealId: string
	): Promise<{ fileId: string; s3Key: string }> => {
		const formData = new FormData();
		formData.append('file', file);

		const config = getGlobalConfig();

		// Deal-scoped upload: records a binding so start-extraction can verify the
		// file belongs to this deal. The `deal-id` header drives the backend's
		// EDITOR/DEAL permission check.
		const response = await apiClient(`${config.apiUrl}/document-parsing/upload`, {
			method: HTTP_METHOD.POST,
			body: formData,
			headers: { 'deal-id': uploadDealId },
		});

		if (!response.ok) {
			throw new Error(`Upload failed: ${response.statusText}`);
		}

		const result = await response.json();
		const fileData = result.payload || result;
		const fileId = fileData.id;
		const s3Key = fileData.s3key;

		if (!fileId) {
			throw new Error('No file ID returned from upload');
		}

		return { fileId, s3Key };
	};

	const _getDownloadUrl = async (
		fileId: string,
		s3Key: string,
		dealId: string
	): Promise<string | null> => {
		type DownloadUrlResponse = {
			downloadUrl?: string;
			payload?: { downloadUrl?: string };
			data?: { downloadUrl?: string };
		};

		const downloadUrlResponse = await makeRequest<DownloadUrlResponse>({
			endpoint: formatUnicorn(API_PATHS.FILE_ID_DEAL_DEALID_DOWNLOAD_PRE_SIGNED_URL_POST, {
				id: fileId,
				dealId,
			}),
			method: HTTP_METHOD.POST,
		});

		const downloadResponse = downloadUrlResponse as DownloadUrlResponse;
		const downloadUrl =
			downloadResponse.downloadUrl ||
			downloadResponse.payload?.downloadUrl ||
			downloadResponse.data?.downloadUrl;

		let finalDownloadUrl: string | null = null;

		if (!downloadUrl) {
			showToastV2({
				state: 'error',
				title: tCapManage('transactions.add_from_document.error_title'),
				message: tCapManage('transactions.add_from_document.download_url_not_found'),
			});
			return null;
		} else {
			finalDownloadUrl = downloadUrl;
		}

		return finalDownloadUrl;
	};

	const _showUploadError = (error: unknown): void => {
		showToastV2({
			state: 'error',
			title: tCapManage('transactions.add_from_document.error_title'),
			message:
				getUserFacingErrorMessage(error) ??
				tCapManage('transactions.add_from_document.upload_failed'),
		});
	};

	type UploadedDocInfo = {
		fileId: string;
		fileUrl: string;
		fileName: string;
		mimeType: string;
		index: number;
	};

	const _uploadOne = async (
		i: number,
		doc: ProcessedDocument,
		uploadDealId: string
	): Promise<UploadedDocInfo | null> => {
		try {
			const { fileId, s3Key } = await _uploadFile(doc.file, uploadDealId);
			const finalDownloadUrl = await _getDownloadUrl(fileId, s3Key, uploadDealId);
			if (!finalDownloadUrl || typeof finalDownloadUrl !== 'string') {
				setDocuments((prev) =>
					prev.map((d, idx) => (idx === i ? { ...d, hasError: true } : d))
				);
				return null;
			}
			setDocuments((prev) =>
				prev.map((d, idx) => (idx === i ? { ...d, fileUrl: finalDownloadUrl } : d))
			);
			return {
				fileId,
				fileUrl: finalDownloadUrl,
				fileName: doc.file.name,
				mimeType: doc.file.type || 'application/pdf',
				index: i,
			};
		} catch (error) {
			setDocuments((prev) =>
				prev.map((d, idx) => (idx === i ? { ...d, hasError: true } : d))
			);
			_showUploadError(error);
			return null;
		}
	};

	const _uploadAllDocuments = async (uploadDealId: string): Promise<UploadedDocInfo[]> => {
		const uploaded: UploadedDocInfo[] = [];
		for (let i = 0; i < documents.length; i++) {
			const doc = documents[i];
			if (doc.analysisResult || doc.hasError) {
				continue;
			}
			setCurrentProcessingIndex(i + 1);
			const result = await _uploadOne(i, doc, uploadDealId);
			if (result) {
				uploaded.push(result);
			}
		}
		return uploaded;
	};

	const getSelectionKey = (
		docIdx: number,
		txIdx: number,
		fieldName: 'buyers' | 'sellers'
	): string => {
		return `${docIdx}-${txIdx}-${fieldName}`;
	};

	const getSelectedValue = (fieldName: 'buyers' | 'sellers'): FieldValue | undefined => {
		if (!analysisResult?.data || analysisResult.data.length === 0) {
			return undefined;
		}

		const currentTransaction = analysisResult.data[selectedTransactionIndex];
		if (!currentTransaction) {
			return undefined;
		}

		const fieldArray = currentTransaction[fieldName];
		if (!Array.isArray(fieldArray) || fieldArray.length === 0) {
			return undefined;
		}

		const selectionKey = getSelectionKey(
			selectedDocumentIndex,
			selectedTransactionIndex,
			fieldName
		);
		const selectedIndex = buyerSellerSelections[selectionKey] ?? 0;

		return fieldArray[selectedIndex];
	};

	const updateSelectedIndex = (fieldName: 'buyers' | 'sellers', newIndex: number): void => {
		const selectionKey = getSelectionKey(
			selectedDocumentIndex,
			selectedTransactionIndex,
			fieldName
		);
		setBuyerSellerSelections((prev) => ({
			...prev,
			[selectionKey]: newIndex,
		}));

		if (analysisResult?.data) {
			const currentTransaction = analysisResult.data[selectedTransactionIndex];
			const fieldArray = currentTransaction?.[fieldName];
			if (Array.isArray(fieldArray) && fieldArray[newIndex]) {
				setEditedValues((prev) => ({
					...prev,
					[fieldName]: fieldArray,
				}));
			}
		}
	};

	const isSelectionRequired = (): boolean => {
		if (!analysisResult?.data || analysisResult.data.length === 0) {
			return false;
		}

		const currentTransaction = analysisResult.data[selectedTransactionIndex];
		if (!currentTransaction) {
			return false;
		}

		const buyersArray = currentTransaction.buyers;
		const sellersArray = currentTransaction.sellers;

		const buyersNeedsSelection = Array.isArray(buyersArray) && buyersArray.length > 1;
		const sellersNeedsSelection = Array.isArray(sellersArray) && sellersArray.length > 1;

		if (buyersNeedsSelection) {
			const buyersKey = getSelectionKey(
				selectedDocumentIndex,
				selectedTransactionIndex,
				'buyers'
			);
			if (buyerSellerSelections[buyersKey] === undefined) {
				return true;
			}
		}

		if (sellersNeedsSelection) {
			const sellersKey = getSelectionKey(
				selectedDocumentIndex,
				selectedTransactionIndex,
				'sellers'
			);
			if (buyerSellerSelections[sellersKey] === undefined) {
				return true;
			}
		}

		return false;
	};

	return {
		documents,
		uploadedFile,
		uploadedFileUrl,
		isProcessing,
		handleFileDrop,
		clearFile,
		removeDocument,
		processDocuments,
		analysisResult,
		processingStep,
		editedValues,
		updateFieldValue,
		getFinalValues,
		getAllTransactions,
		isEditMode,
		setIsEditMode,
		showBulkImport,
		setShowBulkImport,
		selectedDocumentIndex,
		setSelectedDocumentIndex,
		selectedTransactionIndex,
		setSelectedTransactionIndex,
		buyerSellerSelections,
		getSelectedValue,
		updateSelectedIndex,
		isSelectionRequired,
		processedDocumentsCount,
		hasAnyResults,
		currentProcessingIndex,
		currentProcessingDocument,
		// Extraction-specific exports
		isExtractionRunning,
		isExtractionPollingStalled,
		extractionProgress,
		extractionDocuments,
		// Batch exports
		batchId,
		batchTitle,
		setBatchTitle,
		// Batches list
		batches: batchesQuery.data?.batches ?? [],
		isBatchesLoading: batchesQuery.isLoading,
		refetchBatches: batchesQuery.refetch,
		// Batch selection
		selectedBatchId,
		selectedBatchStatus,
		selectBatch,
		deselectBatch,
		batchTransactions: batchTransactionsQuery.data?.documents ?? [],
		isBatchTransactionsLoading: batchTransactionsQuery.isLoading,
	};
};
